import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { DatePicker } from '@heroui/react';
import type { DatePickerProps } from '@heroui/react';
import { I18nProvider } from '@react-aria/i18n';
import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

type VerificationDatePickerProps = Omit<
  DatePickerProps,
  'showMonthAndYearPickers' | 'CalendarTopContent'
> & {
  yearRange?: { min?: number; max?: number };
};

type OpenMenu = 'month' | 'year' | null;

/** Safe cast to bridge duplicate @internationalized/date types in node_modules */
function toCalendarDate(v: unknown): CalendarDate {
  if (!v) return today(getLocalTimeZone());
  const c = v as Record<string, number>;
  return new CalendarDate(c.year, c.month, c.day);
}

/** Constrain day so month/year changes never create an invalid calendar date. */
function withMonthYear(
  date: CalendarDate,
  fields: { month?: number; year?: number }
): CalendarDate {
  const year = fields.year ?? date.year;
  const month = fields.month ?? date.month;
  const firstOfMonth = new CalendarDate(year, month, 1);
  const maxDay = firstOfMonth.calendar.getDaysInMonth(firstOfMonth);
  return new CalendarDate(year, month, Math.min(date.day, maxDay));
}

const triggerBtnClass =
  'flex h-8 w-full items-center justify-between gap-1 rounded-md border border-default-200 bg-content1 px-2 text-left text-sm text-foreground outline-none hover:border-default-400 focus:border-primary';

const menuClass =
  'absolute left-0 top-full z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-default-200 bg-content1 py-1 shadow-md';

/**
 * HeroUI DatePicker with in-popover month/year dropdowns (avoids native
 * &lt;select&gt;, which closes the popover inside a Modal).
 */
const VerificationDatePicker = ({
  value,
  defaultValue,
  calendarProps,
  yearRange,
  onChange,
  minValue,
  maxValue,
  ...props
}: VerificationDatePickerProps) => {
  const currentYear = today(getLocalTimeZone()).year;
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [focusedDate, setFocusedDate] = useState<CalendarDate>(() =>
    toCalendarDate(value ?? defaultValue)
  );
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedOptionRef = useRef<HTMLButtonElement | null>(null);
  const menuListRef = useRef<HTMLUListElement | null>(null);
  const isClosingCalendarRef = useRef(false);

  useEffect(() => {
    if (value) {
      setFocusedDate(toCalendarDate(value));
    }
  }, [value]);

  // Bridge values from props that reference the nested @internationalized/date
  // package by reading primitive fields only.
  const resolvedMinValue = minValue ?? calendarProps?.minValue ?? undefined;
  const resolvedMaxValue = maxValue ?? calendarProps?.maxValue ?? undefined;

  const minYear =
    yearRange?.min ??
    (resolvedMinValue as Record<string, number> | undefined)?.year ??
    1900;
  const maxYear =
    yearRange?.max ??
    (resolvedMaxValue as Record<string, number> | undefined)?.year ??
    currentYear;

  const years = useMemo(() => {
    const list: number[] = [];
    for (let y = maxYear; y >= minYear; y -= 1) {
      list.push(y);
    }
    return list;
  }, [minYear, maxYear]);

  const safeFocused = useMemo(() => {
    const clampedYear = Math.min(maxYear, Math.max(minYear, focusedDate.year));
    if (clampedYear === focusedDate.year) return focusedDate;
    return withMonthYear(focusedDate, { year: clampedYear });
  }, [focusedDate, minYear, maxYear]);

  // Scroll current month/year into the middle of the dropdown when it opens
  useEffect(() => {
    if (!openMenu) return;
    const frame = requestAnimationFrame(() => {
      const list = menuListRef.current;
      const option = selectedOptionRef.current;
      if (!list || !option) return;
      list.scrollTop =
        option.offsetTop - list.clientHeight / 2 + option.clientHeight / 2;
    });
    return () => cancelAnimationFrame(frame);
  }, [openMenu, safeFocused.month, safeFocused.year]);

  const isDateSegmentTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    return (
      target.getAttribute('data-slot') === 'segment' ||
      !!target.closest('[data-slot="segment"]')
    );
  };

  const handleSegmentPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDateSegmentTarget(e.target)) return;
    if (isClosingCalendarRef.current) return;

    const root = rootRef.current;
    if (!root?.querySelector('[data-open="true"]')) return;

    const segment = (e.target as HTMLElement).closest(
      '[data-slot="segment"]'
    ) as HTMLElement | null;

    const toggleBtn =
      (root
        .querySelector('[data-slot="selector-icon"]')
        ?.closest('button') as HTMLButtonElement | null) ??
      (root.querySelector(
        'button[aria-haspopup="dialog"]'
      ) as HTMLButtonElement | null);

    if (!toggleBtn) return;

    isClosingCalendarRef.current = true;
    setOpenMenu(null);

    requestAnimationFrame(() => {
      toggleBtn.click();
      segment?.focus();
      isClosingCalendarRef.current = false;
    });
  };

  const handleFocusChange = useCallback((date: CalendarDate) => {
    setFocusedDate(new CalendarDate(date.year, date.month, date.day));
    setOpenMenu(null);
  }, []);

  const handleMonthSelect = (month: number) => {
    setFocusedDate(withMonthYear(safeFocused, { month }));
    setOpenMenu(null);
  };

  const handleYearSelect = (year: number) => {
    setFocusedDate(withMonthYear(safeFocused, { year }));
    setOpenMenu(null);
  };

  // Use a loose signature to avoid type clash from duplicate @internationalized/date
  const handleChange = (val: unknown) => {
    setOpenMenu(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const date = val as Record<string, number> | null | undefined;
    if (!date || date.year < 1000) return;
    setFocusedDate(toCalendarDate(val));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (onChange as any)?.(val);
  };

  // Uncontrolled DatePicker so segment typing is not fought by parent state.
  const seedValue = (defaultValue ?? value ?? null) as unknown;

  return (
    <I18nProvider locale="en-CA">
      <div ref={rootRef} onPointerDownCapture={handleSegmentPointerDown}>
        <DatePicker
          {...props}
          minValue={resolvedMinValue}
          maxValue={resolvedMaxValue}
          defaultValue={seedValue as never}
          onChange={handleChange as never}
          showMonthAndYearPickers={false}
          variant="bordered"
          calendarProps={
            {
              ...calendarProps,
              focusedValue: safeFocused,
              onFocusChange: (date: CalendarDate) => {
                handleFocusChange(date);
                calendarProps?.onFocusChange?.(date);
              },
              isDateUnavailable: (date: CalendarDate) => {
                if (calendarProps?.isDateUnavailable?.(date)) return true;
                if (
                  resolvedMaxValue &&
                  (date as unknown as CalendarDate).compare(
                    resolvedMaxValue as unknown as CalendarDate
                  ) > 0
                )
                  return true;
                if (
                  resolvedMinValue &&
                  (date as unknown as CalendarDate).compare(
                    resolvedMinValue as unknown as CalendarDate
                  ) < 0
                )
                  return true;
                return false;
              },
              classNames: {
                ...calendarProps?.classNames,
                base: 'overflow-visible',
                headerWrapper: 'px-3 pt-2',
                content: 'overflow-visible',
                title: 'text-sm font-medium',
              },
            } as never
          }
          CalendarTopContent={
            <div
              className="relative z-20 flex w-full gap-2 px-3 pb-2"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="relative min-w-0 flex-1">
                <button
                  type="button"
                  aria-label="Month"
                  aria-expanded={openMenu === 'month'}
                  className={triggerBtnClass}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                    setOpenMenu((prev) => (prev === 'month' ? null : 'month'))
                  }
                >
                  <span className="truncate">
                    {MONTH_LABELS[safeFocused.month - 1]}
                  </span>
                  <span className="text-default-400">{'>'}</span>
                </button>
                {openMenu === 'month' && (
                  <ul
                    ref={menuListRef}
                    className={menuClass}
                    role="listbox"
                    aria-label="Select month"
                  >
                    {MONTH_LABELS.map((label, index) => {
                      const month = index + 1;
                      const selected = month === safeFocused.month;
                      return (
                        <li key={label} role="option" aria-selected={selected}>
                          <button
                            ref={selected ? selectedOptionRef : undefined}
                            type="button"
                            className={`flex w-full px-3 py-1.5 text-left text-sm hover:bg-default-100 ${
                              selected
                                ? 'bg-primary/10 font-medium text-primary'
                                : 'text-foreground'
                            }`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleMonthSelect(month)}
                          >
                            {label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="relative w-[7.5rem] shrink-0">
                <button
                  type="button"
                  aria-label="Year"
                  aria-expanded={openMenu === 'year'}
                  className={triggerBtnClass}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                    setOpenMenu((prev) => (prev === 'year' ? null : 'year'))
                  }
                >
                  <span>{safeFocused.year}</span>
                  <span className="text-default-400">{'>'}</span>
                </button>
                {openMenu === 'year' && (
                  <ul
                    ref={menuListRef}
                    className={menuClass}
                    role="listbox"
                    aria-label="Select year"
                  >
                    {years.map((year) => {
                      const selected = year === safeFocused.year;
                      return (
                        <li key={year} role="option" aria-selected={selected}>
                          <button
                            ref={selected ? selectedOptionRef : undefined}
                            type="button"
                            className={`flex w-full px-3 py-1.5 text-left text-sm hover:bg-default-100 ${
                              selected
                                ? 'bg-primary/10 font-medium text-primary'
                                : 'text-foreground'
                            }`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleYearSelect(year)}
                          >
                            {year}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          }
        />
      </div>
    </I18nProvider>
  );
};

export default VerificationDatePicker;
